import React, { FunctionComponent, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { Grid, GridItem } from "components/Grid"
import Button from "components/Button/Button"
import { Flex } from "components/Flex"
import { Switch, SwitchOption } from "components/Switch"
import Modal from "components/Modal/Modal"
import Select from "components/Select/Select"

import { MeasurementSystem } from "config/constants"
import Unit from "models/Unit"

interface IUnitPickerModalProps {
    units: Unit[]
    defaultUnit?: Unit | null
    onChange(selectedUnit: Unit): void
    onClose(): void
}

const UnitPickerModal: FunctionComponent<React.PropsWithChildren<IUnitPickerModalProps>> = ({
    units,
    defaultUnit,
    onChange,
    onClose,
}) => {
    const { t } = useTranslation()
    const [currentSystem, setCurrentSystem] = useState<MeasurementSystem | null>(MeasurementSystem.metric as MeasurementSystem)
    const [selectedUnit, setSelectedUnit] = useState<Unit | null>()

    useEffect(() => {
        setCurrentSystem(defaultUnit?.system || null)
        setSelectedUnit(defaultUnit || null)
    }, [defaultUnit])


    function getUnitsDropdownOptions() {
        return getCurrentSystemUnits()
            .map(u => ({
                label: currentSystem === MeasurementSystem.metric ? `${t('unit.unit')}: ${t(`units.${u.unit}` as any)} (${u.unit})` : `${t(`units.${u.unit}` as any)} (${u.unit})`,
                value: u.id
            }))
    }

    function getCurrentSystemUnits(system = currentSystem) {
        return units
            .filter(u => u.system === system)
    }

    function onSystemChange(system: MeasurementSystem) {
        setCurrentSystem(system)
        setSelectedUnit(getCurrentSystemUnits(system)[0])
    }

    function onUnitChange(unitId: string) {
        const unit = getCurrentSystemUnits().find(u => u.id === unitId)
        if (!unit) return
        setSelectedUnit(unit)
    }

    function onApplyClick() {
        onClose && onClose()
        if (selectedUnit) {
            onChange(selectedUnit)
        }
    }

    return (
        <Modal
            title={t('unit.changeUnit')}
            maxWidth="450px"
            onClose={onClose}
            footer={
                <Flex justifyContent="flex-end">
                    <Button
                        theme="secondary"
                        label={t('common.cancel')}
                        onClick={onClose}
                        data-testid="unit-change-modal-cancel" />
                    <Button
                        label={t('common.apply')}
                        onClick={onApplyClick}
                        className="ml-2"
                        data-testid="unit-change-modal-apply" />
                </Flex>
            }>
            <Grid
                columns="auto 1fr"
                gap="2.4rem">
                <GridItem>
                    <Switch
                        value={currentSystem}
                        onSwitch={value => onSystemChange(value)}>
                        <SwitchOption value={MeasurementSystem.metric}>
                            {t('unit.metric')}
                        </SwitchOption>
                        <SwitchOption value={MeasurementSystem.imperial}>
                            {t('unit.imperial')}
                        </SwitchOption>
                    </Switch>
                </GridItem>
                <GridItem>
                    <Select
                        value={selectedUnit?.id}
                        options={getUnitsDropdownOptions()}
                        onChange={item => onUnitChange(item!.value)}
                        isInModal />
                </GridItem>
            </Grid>
        </Modal>
    )
}

export default UnitPickerModal
